import { useCallback, useMemo } from 'react'
import type { Location, NavigateFunction } from 'react-router-dom'
import type { ForgotPasswordPayload, ResetPasswordPayload } from '../../types/auth'
import type { StatusMessage } from '../../types/status'
import { forgotPassword, resetPassword } from '../../api/authApi'
import { validateResetPasswordForm } from '../../utils/authFormValidation'
import { resolveApiErrorMessage, resolveResetToken } from './shared'

interface UsePasswordResetFlowParams {
  apiBaseUrl: string
  navigate: NavigateFunction
  location: Location
  setAuthStatus: (status: StatusMessage | null) => void
}

export interface UsePasswordResetFlowResult {
  isResetPasswordFlow: boolean
  initialResetToken: string
  legacyResetUrl: string
  handleForgotPassword: (payload: ForgotPasswordPayload) => Promise<void>
  handleResetPassword: (payload: ResetPasswordPayload) => Promise<void>
}

export function usePasswordResetFlow({
  apiBaseUrl,
  navigate,
  location,
  setAuthStatus
}: UsePasswordResetFlowParams): UsePasswordResetFlowResult {
  const isResetPasswordFlow = useMemo(() => {
    const query = new URLSearchParams(location.search)
    const legacyView = query.get('view')
    return location.pathname === '/reset-password' || legacyView === 'reset-password'
  }, [location.pathname, location.search])

  const initialResetToken = useMemo(() => {
    const token = resolveResetToken(location.search)
    return isResetPasswordFlow ? token : ''
  }, [isResetPasswordFlow, location.search])

  const legacyResetUrl = useMemo(() => {
    const query = new URLSearchParams(location.search)
    const view = query.get('view')
    const token = resolveResetToken(location.search)
    if (view !== 'reset-password') {
      return '/login'
    }

    return token ? `/login?view=reset-password&token=${encodeURIComponent(token)}` : '/login?view=reset-password'
  }, [location.search])

  const handleForgotPassword = useCallback(async ({ email }: ForgotPasswordPayload): Promise<void> => {
    setAuthStatus({ kind: 'loading', message: 'Отправка инструкции на e-mail...' })

    try {
      const payload = await forgotPassword(apiBaseUrl, { email })

      setAuthStatus({
        kind: 'success',
        message: payload?.message || 'Если пользователь с таким e-mail существует, инструкция отправлена на почту.'
      })
    } catch (error) {
      setAuthStatus({
        kind: 'error',
        message: resolveApiErrorMessage(error, apiBaseUrl, 'Не удалось отправить инструкцию для сброса пароля.')
      })
    }
  }, [apiBaseUrl, setAuthStatus])

  const handleResetPassword = useCallback(async ({ token, newPassword, confirmNewPassword }: ResetPasswordPayload): Promise<void> => {
    const validationError = validateResetPasswordForm({ token, newPassword, confirmNewPassword })
    if (validationError) {
      setAuthStatus({ kind: 'error', message: validationError })
      return
    }

    setAuthStatus({ kind: 'loading', message: 'Обновление пароля...' })

    try {
      const payload = await resetPassword(apiBaseUrl, { token, newPassword })

      setAuthStatus({
        kind: 'success',
        message: payload?.message || 'Пароль успешно обновлен. Теперь можно выполнить вход.'
      })
      navigate('/login', { replace: true })
    } catch (error) {
      setAuthStatus({
        kind: 'error',
        message: resolveApiErrorMessage(error, apiBaseUrl, 'Не удалось обновить пароль.')
      })
    }
  }, [apiBaseUrl, navigate, setAuthStatus])

  return {
    isResetPasswordFlow,
    initialResetToken,
    legacyResetUrl,
    handleForgotPassword,
    handleResetPassword
  }
}
